import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import GameLogTable from '../components/GameLogTable';
import './PlayerPage.css';

/**
 * PlayerPage Component
 * Individual player page with profile and game log
 */

const PlayerPage = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPlayerData();
  }, [playerId]);

  const loadPlayerData = async () => {
    try {
      setLoading(true);
      const [playerResponse, gamesResponse] = await Promise.all([
        ApiService.getPlayer(playerId),
        ApiService.getPlayerGames(playerId)
      ]);

      setPlayer(playerResponse.data);
      setGames(gamesResponse.data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load player data. Please try again.');
      console.error('Error loading player:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="player-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading player...</p>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="player-page">
        <div className="error-state">
          <h2>⚠️ Player not found</h2>
          <button onClick={() => navigate('/')} className="back-button">
            Back to Directory
          </button>
        </div>
      </div>
    );
  }

  const photoUrl = player.Photo_URL 
    ? ApiService.getPlayerPhotoUrl(player.Photo_URL)
    : '/assets/default-player.png';

  const badges = player.badges || [];

  return (
    <div className="player-page">
      <button onClick={() => navigate('/')} className="back-button">
        ← Back to Directory
      </button>

      <div className="player-profile-section">
        <div className="profile-card">
          <div className="profile-header">
            <h1 className="player-name-large">{player.Player_Name || player.Player_ID}</h1>
            <div className="player-ranking-large">
              {player.Ranking ? `#${player.Ranking}` : 'Unranked'}
            </div>
          </div>

          <div className="profile-body">
            <div className="profile-photo-section">
              <div className="profile-photo">
                <img 
                  src={photoUrl} 
                  alt={player.Player_Name}
                  onError={(e) => e.target.src = '/assets/default-player.png'}
                />
              </div>
            </div>

            <div className="profile-info-section">
              <div className="info-grid-large">
                <div className="info-item-large">
                  <label>Age</label>
                  <span>{player.Age || 'N/A'}</span>
                </div>
                <div className="info-item-large">
                  <label>Country</label>
                  <span>{player.Country || 'N/A'}</span>
                </div>
                <div className="info-item-large">
                  <label>State</label>
                  <span>{player.State || 'N/A'}</span>
                </div>
                <div className="info-item-large">
                  <label>City</label>
                  <span>{player.City || 'N/A'}</span>
                </div>
                <div className="info-item-large">
                  <label>Height</label>
                  <span>{player.Height || 'N/A'}</span>
                </div>
                <div className="info-item-large">
                  <label>Weight</label>
                  <span>{player.Weight || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="profile-stats-section">
            <div className="stat-box">
              <label>Record</label>
              <div className="record-display">
                <span className="wins">{player.Wins || 0}W</span>
                <span className="separator">-</span>
                <span className="undecided">{player.Undecided || 0}U</span>
                <span className="separator">-</span>
                <span className="losses">{player.Losses || 0}L</span>
              </div>
            </div>
            <div className="stat-box">
              <label>Win %</label>
              <span className="stat-value">
                {player.Win_Percentage ? `${(player.Win_Percentage * 100).toFixed(1)}%` : '0%'}
              </span>
            </div>
            <div className="stat-box">
              <label>Total Games</label>
              <span className="stat-value">{player.Total_Games || 0}</span>
            </div>
            <div className="stat-box">
              <label>Class</label>
              <span className="stat-value">{player.Class || 'N/A'}</span>
            </div>
            <div className="stat-box">
              <label>Platform</label>
              <span className="stat-value">{player.Main_Platform || 'N/A'}</span>
            </div>
            <div className="stat-box">
              <label>Highest Level</label>
              <span className="stat-value">{player.Highest_Level || 'N/A'}</span>
            </div>
          </div>

          {badges.length > 0 && (
            <div className="profile-badges-section">
              <label>Badges</label>
              <div className="badges-display">
                {badges.map((badgeId) => (
                  <div key={badgeId} className="badge-large">
                    <img 
                      src={ApiService.getBadgeImageUrl(badgeId)} 
                      alt={`Badge ${badgeId}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <GameLogTable games={games} />
    </div>
  );
};

export default PlayerPage;
